import { languageFlags as flags, languageNames, rememberLanguage } from '../../../utils/languagePreference';
import LoadFailure from '../../../components/Brand/LoadFailure';
import { manageLegalConsent } from '../../../legal/consent';
import { legalCopy } from '../../../legal/copy';
import { showConsentRequired } from '../../../legal/requiredNotice';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout } from 'antd';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { EditIcon, LogoutIcon, TrashIcon, CleanIcon, EyeOffIcon, LocationIcon,
  ChevronUpIcon, BackIcon, SaveIcon, AlertCircleIcon } from '../../../components/Brand/Icon';
import usePageLayout from '../../../hooks/usePageLayout';
import useSessionIdentity, { getSessionIdentity } from '../../../hooks/useSessionIdentity';
import changeLanguageInOrder from '../../../utils/changeLanguageInOrder';
import { clearSearchCache } from '../../../hooks/useSearch';
import { invalidateFeedSnapshots } from '../../../utils/searchFeedReturn';
import ButtonPanelReaction from '../../../components/Buttons/ButtonPanelReaction';
import WiwHeaderTitle from '../../../components/WiwHeaderTitle';
import UserImage from '../../../components/UserImage';
import BrandLoader from '../../../components/Brand/BrandLoader';
import ControlButton from '../../../components/WiwFooter/ControlButton';
import ConfirmationPanel from '../../../components/ConfirmationPanel';
import message from '../../../components/Brand/brandMessage';
import { getUser, removeUser, setLocalStorage, handleImageUpload } from '../../../utils/func';
import { IMAGE_PICKER_ACCEPT } from '../../../utils/imageUpload';
import url from '../../../utils/url';
import config from '../../../utils/config';
import api from '../../../services';
import { APP_ROUTES } from '../../../utils/constant';
import '../UserResponsive.scss';
import './Settings.scss';
import SettingsPicker from './SettingsPicker';
import { profileFieldError, profileErrorMessage } from './profileValidation';
import { useDisclosureTransition } from './useDisclosureTransition';
import useNotificationToggle from '../../../notifications/useNotificationToggle';
import DevicePushStatus from '../../../notifications/DevicePushStatus';
import AccountProtection from '../../../accountProtection/AccountProtection';
import { readNotificationPreferences } from '../../../notifications/preferenceWrites';
import useLocationPolicy from '../../../location/useLocationPolicy';
import { getDeviceLocation, canOpenLocationSettings, openLocationSettings } from '../../../location/deviceLocation';
import { createReceipt, submitDeletion } from '../../../accountDeletion/receipt';
import { deletionCopy } from '../../../accountDeletion/copy';
import { appLocalStorage } from '../../../utils/storage';
import { confirmSystemAction } from '../../../utils/systemConfirmation';

const { Header, Content, Footer } = Layout;
const accountActions = {
  exit: { call: api.userLogout, icon: LogoutIcon, prompt: 'confirmExit', action: 'logout' },
  exit_all: { call: api.userLogoutAll, icon: LogoutIcon, prompt: 'confirmExitAll', action: 'logout' },
  hide_opinions: { call: api.reactionHide, icon: EyeOffIcon, prompt: 'confirmHide', action: 'hide' },
  clear_opinions: { call: api.reactionClear, icon: CleanIcon, prompt: 'confirmClear', action: 'clear' },
  delete: { call: api.userDelete, icon: TrashIcon, prompt: 'confirmDelete', action: 'delete' },
};
const invalidateAccountViews = () => {
  invalidateFeedSnapshots();
  clearSearchCache();
};

function Row({ id, label, value, icon = <EditIcon />, onClick, expanded, disabled, children }) {
  return <div className="settings-row" data-setting={id}>
    <button type="button" className="settings-row-label" onClick={onClick} disabled={disabled}
      aria-label={label} aria-expanded={expanded} aria-controls={expanded === undefined ? undefined : `settings-${id}-panel`}>
      <span>{value || label}</span>
    </button>
    {children || <button type="button" className="settings-icon-button" onClick={onClick} disabled={disabled}
      aria-label={label} aria-expanded={expanded} aria-controls={expanded === undefined ? undefined : `settings-${id}-panel`}>{icon}</button>}
  </div>;
}
function Disclosure({ id, label, open, children }) {
  // Keep the outgoing content intact until its collapse finishes (including
  // a pending confirmation). Clearing it early changes the starting height.
  const content = useRef(children);
  if (open) content.current = children;
  const panel = useDisclosureTransition(open, { expand: true, onEntered: element => {
    element.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  } });
  return panel.present ? <div ref={panel.ref} id={`settings-${id}-panel`} role="group" aria-label={label}
    className="settings-panel" aria-hidden={!open || undefined} inert={!open ? '' : undefined}>
    {content.current}
  </div> : null;
}

export default function UserSettings() {
  const session = useSessionIdentity();
  const navigate = useNavigate();
  const leaving = useRef(false);
  const mounted = useRef(true);
  const sourceEntry = useRef({ href: window.location.href, key: window.history.state?.key });
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const leaveAccount = ({ target = APP_ROUTES.MAIN, keepSession = false } = {}) => {
    // Accepted deletion clears the session before its promise settles. App's
    // session-keyed provider can remount this screen during that cleanup.
    // Allow only the captured history entry's guest to reach its receipt;
    // an actual navigation or another signed-in account must keep its screen.
    const completedDeletion = target === '/account-deletion' && !getUser() && !appLocalStorage.getItem(config.token)
      && sourceEntry.current.href === window.location.href && sourceEntry.current.key === window.history.state?.key;
    if (!mounted.current && !completedDeletion) return;
    // Clearing storage notifies useSyncExternalStore synchronously. Mark the
    // intentional exit first so its guest render cannot start another private
    // profile read and race the feed navigation with a later 401 redirect.
    leaving.current = true;
    try {
      if (!keepSession) removeUser();
    } finally {
      // The server has already ended this session. Storage restrictions must
      // not strand the user on the departing private screen.
      if (target === '/account-deletion') navigate(url(target), { replace: true });
      else navigate(url(target));
    }
  };
  return leaving.current ? null : <SessionSettings key={session} session={session} onLeaveAccount={leaveAccount} />;
}

function SessionSettings({ session, onLeaveAccount }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [draft, setDraft] = useState({});
  const [languages, setLanguages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [notificationDraft, setNotificationDraft] = useState({});
  const [inAppDraft, setInAppDraft] = useState({});
  const [countryOpen, setCountryOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialError, setInitialError] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(() => location.state?.settingsSection === 'account' ? 'account' : null);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [warning, setWarning] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  const ownsSession = () => session === getSessionIdentity();
  const isCurrent = request => request === generation.current && ownsSession();
  const readController = useRef(null);
  const originalUsername = useRef(null);
  const pending = useRef(false);
  const geoGeneration = useRef(0);
  const dismiss = useRef(null);
  const fileInput = useRef(null);
  const feedbackRef = useRef(null);
  const page = useRef(null);
  const header = useRef(null);
  const footer = useRef(null);
  const { contentHeight, contentTop } = usePageLayout(header, footer);
  const s = key => t(`settings:${key}`);
  const countryPolicy = useLocationPolicy(open === 'location');
  useEffect(() => {
    if (!countryPolicy.policy?.available) return;
    const country_code = countryPolicy.policy.country_code;
    setUser(previous => previous ? { ...previous, country_code } : previous);
    setDraft(previous => ({ ...previous, country_code }));
  }, [countryPolicy.policy]);

  const { toggleNotification, toggleInApp } = useNotificationToggle({ active: open === 'notifications',
    draft: notificationDraft, inAppDraft, preferences, setPreferences,
    setDraft: setNotificationDraft, setInAppDraft, setFeedback, generation });

  useEffect(() => {
    const request = ++generation.current;
    const controller = new AbortController();
    readController.current = controller;
    pending.current = false;
    setLoading(true);
    setInitialError(false);
    // Preferences load independently: their recovery must not hold name,
    // avatar, country or language editing behind an optional endpoint.
    setPreferences(null);
    setPreferencesLoading(true);
    readNotificationPreferences({ signal: controller.signal }).then(result => {
      if (request !== generation.current || session !== getSessionIdentity()) return;
      const value = result?.success && result.preferences ? result.preferences : null;
      setPreferences(value);
      setNotificationDraft({ ...value?.notifications });
      setInAppDraft({ ...value?.in_app });
    }).catch(() => {}).finally(() => {
      if (request === generation.current && session === getSessionIdentity()) setPreferencesLoading(false);
    });
    Promise.all([api.userByToken(undefined, { signal: controller.signal }),
      api.getLng(undefined, { signal: controller.signal }), api.getCountry(undefined, { signal: controller.signal })]).then(([profile, lng, country]) => {
      if (request !== generation.current || session !== getSessionIdentity()) return;
      if (profile?.statusCode === 401 || (profile?.success && !profile.user)) {
        navigate(url(APP_ROUTES.PAGE403), { replace: true });
        return;
      }
      if (!profile?.success || !profile.user?.id || !lng?.success || !country?.success ||
        !Array.isArray(lng.list) || !Array.isArray(country.list)) throw new Error('Settings unavailable');
      setUser(profile.user);
      if (originalUsername.current === null) originalUsername.current = profile.user.user_name;
      setDraft(profile.user);
      setLanguages(lng.list);
      setCountries(country.list);

    }).catch(() => {
      if (request === generation.current && session === getSessionIdentity()) setInitialError(true);
    }).finally(() => {
      if (request === generation.current && session === getSessionIdentity()) setLoading(false);
    });
    return () => { controller.abort(); geoGeneration.current += 1; if (request === generation.current) generation.current += 1; };
  }, [attempt, navigate, session]);

  const focusGroup = languageOpen ? 'language' : ['location', 'notifications', 'opinions', 'account'].includes(open) ? open : null;
  useEffect(() => {
    const click = event => {
      if (warning || event.target.closest('.wiw-message, .legal-consent-overlay')) return;
      if (!focusGroup || event.target.closest(`[data-settings-group="${focusGroup}"]`) || feedbackRef.current?.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      dismiss.current?.();
    };
    const key = event => {
      if (warning || event.target.closest('.legal-consent-overlay')) return;
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key !== 'Escape' || (!open && !languageOpen)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (countryOpen) { setCountryOpen(false); page.current?.querySelector('#settings-country')?.focus({ preventScroll: true }); }
      else dismiss.current?.();
    };
    document.addEventListener('click', click, true);
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('click', click, true); document.removeEventListener('keydown', key, true); };
  }, [focusGroup, open, languageOpen, countryOpen, warning]);

  useEffect(() => {
    if (!['name', 'username'].includes(open)) return;
    const input = page.current?.querySelector(`[data-settings-group="${open}"] input`);
    input?.focus({ preventScroll: true });
    input?.setSelectionRange(input.value.length, input.value.length);
  }, [open]);

  useEffect(() => {
    if (!feedback) return;
    feedbackRef.current?.focus({ preventScroll: true });
    feedbackRef.current?.scrollIntoView({ block: 'nearest' });
  }, [feedback]);

  const countryOptions = useMemo(() => countries.map(item => ({ value: item.code,
    flag: /^[a-z]{2}$/i.test(item.code) ? String.fromCodePoint(...item.code.toUpperCase().split('').map(letter => 127397 + letter.charCodeAt(0))) : '',
    label: item[i18n.resolvedLanguage] || item.en || item.code,
  })), [countries, i18n.resolvedLanguage]);
  const languageOptions = useMemo(() => languages.map(item => ({ value: item.lng_code,
    label: `${flags[item.lng_code] || ''} ${languageNames[item.lng_code] || item.button_text}`,
    shortLabel: `${flags[item.lng_code] || ''} ${item.lng_code.toUpperCase()}`,
  })), [languages]);

  const toggle = key => {
    if (pending.current) return;
    if (open === key) { dismiss.current?.(); return; }
    geoGeneration.current += 1; setLocating(false); setCountryOpen(false);
    setDraft({ ...user });
    setWarning(null);
    setFeedback('');
    setLanguageOpen(false);
    setOpen(key);
  };
  const updateCachedUser = changes => {
    if (!ownsSession()) return;
    const updated = { ...user, ...changes };
    setUser(updated);
    setLocalStorage(config.user, JSON.stringify({ ...getUser(), ...updated }));
    invalidateAccountViews();
  };
  const run = async (key, action, success) => {
    if (pending.current || !ownsSession()) return;
    pending.current = true;
    const request = generation.current;
    setBusy(key);
    setFeedback('');
    try {
      const result = await action();
      if (!isCurrent(request)) return;
      if (result?.cancelled) return;
      if (showConsentRequired(result)) return;
      if (!result?.success) {
        const validation = profileErrorMessage(result?.errorCode);
        if (validation) { setFeedback(validation); return; }
        if (key === 'location' && result?.errorCode?.startsWith('LOCATION_')) {
          await countryPolicy.refresh();
          if (isCurrent(request)) setFeedback(result.errorCode === 'LOCATION_CHANGE_LOCKED' ? ''
            : result.errorCode === 'LOCATION_CONFIRMATION_REQUIRED' ? 'location:confirmationNeeded' : 'location:policyUnavailable');
          return;
        }
        throw new Error('Settings update failed');
      }
      await success(result);
    } catch (error) {
      if (isCurrent(request) && !showConsentRequired(error)) setFeedback(key === 'photo'
        ? (error?.code === 'IMAGE_UNREADABLE' ? 'brand:imageFormatError' : 'brand:imageError') : 'brand:saveError');
    } finally {
      if (isCurrent(request)) { pending.current = false; setBusy(''); }
    }
  };
  const saveFields = changes => {
    if (Object.entries(changes).some(([field, value]) => profileFieldError(field, value))) return;
    return run(changes.lng_code ? 'language' : 'profile', () => api.userUpdateFields({ ...changes, id: user.id }), async result => {
    updateCachedUser({ ...changes, ...result.user });
    setOpen(null);
    if (changes.lng_code) {
      await changeLanguageInOrder(i18n, changes.lng_code, ownsSession);
      if (ownsSession()) rememberLanguage(changes.lng_code);
    }
    });
  };
  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const request = generation.current;
    await run('photo', async () => {
      const uploaded = await handleImageUpload(file, { signal: readController.current?.signal });
      if (!uploaded?.data?.link) throw new Error('Upload failed');
      if (!isCurrent(request)) return null;
      const result = await api.userUpdateFields({ id: user.id, picture: uploaded.data.link });
      return { ...result, picture: uploaded.data.link };
    }, result => { updateCachedUser(result.user || { picture: result.picture }); });
  };
  const canSaveLocation = Boolean(countryPolicy.allowed && draft.country_code && draft.country_code !== user?.country_code);
  const saveLocation = () => {
    if (!canSaveLocation) return;
    geoGeneration.current += 1; setLocating(false);
    return run('location', () => api.userPreferencesSave({ country_code: draft.country_code, location_name: null,
      ...(countryPolicy.confirmation ? { location_confirmation: countryPolicy.confirmation } : {}) }), result => {
      setPreferences(result.preferences);
      updateCachedUser({ country_code: draft.country_code });
      setCountryOpen(false); setOpen(null);
    });
  };
  const closeGroup = () => {
    if (pending.current) return;
    const closing = languageOpen ? 'language' : open;
    const scroller = page.current?.querySelector('.settings-content');
    scroller?.scrollTo({ top: scroller.scrollTop, behavior: 'instant' });
    geoGeneration.current += 1; setLocating(false);
    setWarning(null); setCountryOpen(false); setLanguageOpen(false); setOpen(null);
    requestAnimationFrame(() => page.current?.querySelector(`[data-setting="${closing}"] button`)?.focus({ preventScroll: true }));
  };
  dismiss.current = closeGroup;
  const detectCountry = async () => {
    if (locating || pending.current || !countryPolicy.allowed || !ownsSession()) return;
    setCountryOpen(false); setFeedback('');
    const request = ++geoGeneration.current;
    setLocating(true);
    const currentLocation = () => request === geoGeneration.current && ownsSession();
    const fail = key => { if (currentLocation()) { setLocating(false); setFeedback(`settings:${key}`); } };
    try {
      const position = await getDeviceLocation();
      if (!currentLocation()) return;
      try {
        const response = await api.userLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        if (!currentLocation()) return;
        if (!response?.success) { fail(response?.statusCode === 422 ? 'locationNotFound' : 'locationUnavailable'); return; }
        const country = countries.find(item => item.code.toUpperCase() === response.country_code?.toUpperCase());
        if (!country) { fail('locationNotFound'); return; }
        setDraft(previous => ({ ...previous, country_code: country.code }));
        setLocating(false);
      } catch { fail('locationUnavailable'); }
    } catch (error) {
      if (!currentLocation()) return;
      setLocating(false);
      setFeedback(error.message === 'LOCATION_DENIED' ? 'settings:locationDenied'
        : error.message === 'LOCATION_DISABLED' ? 'location:locationDisabled'
        : error.message === 'LOCATION_TIMEOUT' ? 'location:locationTimeout' : 'settings:locationUnavailable');
    }
  };
  const performAccountAction = async action => {
    if (!action) return;
    if (action === 'delete') {
      // Persist a status capability BEFORE sending. A lost HTTP response cannot
      // become a cancellation or force the user to authenticate to check status.
      if (pending.current || !ownsSession()) return;
      const request = generation.current;
      const capturedToken = appLocalStorage.getItem(config.token);
      pending.current = true; setBusy('delete'); setFeedback('');
      try {
        const receipt = await createReceipt(user.id);
        if (!isCurrent(request)) return;
        try { await submitDeletion(receipt, capturedToken); } catch { /* receipt route resolves an uncertain outcome */ }
        // cleanup may already have logged out the captured owner. Never send a
        // different account that signed in concurrently to this receipt page.
        if (ownsSession() || (!getUser() && !appLocalStorage.getItem(config.token))) {
          onLeaveAccount({ target: '/account-deletion', keepSession: true });
        }
      } catch { if (isCurrent(request)) setFeedback('brand:saveError'); }
      finally { if (isCurrent(request)) { pending.current = false; setBusy(''); } }
      return;
    }
    return run(action, () => accountActions[action].call(), () => {
      invalidateAccountViews();
      setWarning(null);
      if (['exit', 'exit_all', 'delete'].includes(action)) {
        onLeaveAccount();
      } else message.success(s('saved'));
    });
  };
  const requestAccountAction = async action => {
    if (pending.current || !ownsSession()) return;
    if (action === 'exit') { setWarning(previous => previous === action ? null : action); return; }
    const request = generation.current;
    pending.current = true;
    setBusy(action);
    setFeedback('');
    try {
      const confirmed = await confirmSystemAction({
        title: t(`user.btn_${action}`),
        message: action === 'delete' ? `${deletionCopy(i18n.language).confirm} ${deletionCopy(i18n.language).consequences}`
          : s(accountActions[action].prompt),
        confirmLabel: t(`user.btn_${action}`), cancelLabel: t('brand:cancel'),
        destructive: action === 'delete' || action === 'clear_opinions',
      });
      // A native alert may outlive this view or the session that opened it.
      if (!confirmed || !isCurrent(request)) return;
      pending.current = false;
      await performAccountAction(action);
    } catch { if (isCurrent(request)) setFeedback('brand:saveError'); }
    finally { if (isCurrent(request)) { pending.current = false; setBusy(''); } }
  };
  const retryPreferences = async () => {
    if (pending.current || !ownsSession()) return;
    const request = generation.current;
    pending.current = true;
    setBusy('preferences');
    try {
      const result = await readNotificationPreferences({ signal: readController.current?.signal });
      if (isCurrent(request) && result?.success && result.preferences) {
        setPreferences(result.preferences);
        if (open === 'notifications') {
          setNotificationDraft({ ...result.preferences.notifications });
          setInAppDraft({ ...result.preferences.in_app });
        }
      }
    } catch { /* Keep the same read failure panel; never treat this as a failed save. */ }
    finally { if (isCurrent(request)) { pending.current = false; setBusy(null); } }
  };
  const unavailable = preferencesLoading || busy === 'preferences' ? <BrandLoader /> : <LoadFailure onRetry={retryPreferences} recoverOnResume />;
  const groupProps = key => ({ 'data-settings-group': key, 'data-inactive': Boolean(focusGroup && focusGroup !== key),
    inert: focusGroup && focusGroup !== key ? '' : undefined });
  const disclosure = (key, label, icon, contents, value) => <section className="settings-section" key={key} {...groupProps(key)}>
    <Row id={key} label={label} value={value} onClick={() => toggle(key)} expanded={open === key} disabled={Boolean(busy)}
      icon={open === key ? <ChevronUpIcon /> : icon} />
    <Disclosure id={key} label={label} open={open === key}>{contents}</Disclosure>
  </section>;
  const editField = (key, field) => <section className="settings-section" key={key} {...groupProps(key)}>
    {open === key ? <div className="settings-row" data-setting={key}>
      <input id={`settings-${field}`} className="settings-inline-input" aria-label={s(key)} placeholder={s(key)} value={draft[field] || ''}
        aria-invalid={Boolean(profileFieldError(field, draft[field]))}
        aria-describedby={profileFieldError(field, draft[field]) ? `settings-${field}-error` : undefined}
        autoCapitalize={field === 'user_name' ? 'none' : 'words'} disabled={Boolean(busy)}
        onChange={event => { setFeedback(''); setDraft(previous => ({ ...previous, [field]: event.target.value })); }}
        onKeyDown={event => { if (!event.nativeEvent.isComposing && event.keyCode !== 229 && event.key === 'Enter') { event.preventDefault(); if (draft[field]?.trim()) saveFields({ [field]: draft[field].trim() }); } }} />
      <button type="button" className="settings-icon-button" aria-label={t('common.save', { defaultValue: 'Save' })}
        aria-busy={busy === 'profile'}
        disabled={Boolean(busy) || !draft[field]?.trim() || Boolean(profileFieldError(field, draft[field]))} onClick={() => saveFields({ [field]: draft[field].trim() })}>
        <SaveIcon />
      </button>
    </div> : <Row id={key} label={s(key)} value={user?.[field]} onClick={() => toggle(key)} disabled={Boolean(busy)} />}
    {open === key && profileFieldError(field, draft[field]) && <p id={`settings-${field}-error`} className="settings-note" role="alert">
      {t(profileFieldError(field, draft[field]))}
    </p>}
  </section>;
  const actions = names => <div className="settings-actions">{names.map(name => {
    const Icon = accountActions[name].icon;
    return <div key={name}>
      <button type="button" className={`settings-action ${name === 'delete' ? 'settings-action-danger' : ''}`}
        disabled={Boolean(busy)} onClick={() => requestAccountAction(name)}
        aria-expanded={name === 'exit' ? warning === name : undefined}
        aria-haspopup={name === 'exit' ? undefined : 'dialog'} aria-busy={busy === name || undefined}>
        <span>{t(`user.btn_${name}`)}</span><Icon />
      </button>
      {warning === name && <ConfirmationPanel className="settings-confirm" question={name === 'delete'
        ? `${deletionCopy(i18n.language).confirm} ${deletionCopy(i18n.language).consequences}` : s(accountActions[name].prompt)}
        action={accountActions[name].action}
        onOK={() => performAccountAction(warning)} onCancel={() => setWarning(null)} busy={Boolean(busy)} confirmLabel={t(`user.btn_${name}`)} />}
    </div>;
  })}</div>;

  return <Layout className="app-layout user-page account-settings" ref={page}>
    <Helmet><title>{t('user.settings')}</title></Helmet>
    {busy === 'photo' && <BrandLoader className="settings-photo-loading" label={t('brand:loading')} />}
    <Header className="header" ref={header} data-inactive={Boolean(focusGroup)} inert={focusGroup ? '' : undefined}>
      <WiwHeaderTitle title={t('user.settings')} onBack={user?.user_name && user.user_name !== originalUsername.current
        ? () => navigate(url(`${APP_ROUTES.USER}/${encodeURIComponent(user.user_name)}`), { replace: true, state: { from: url(APP_ROUTES.MAIN) } }) : undefined} />
    </Header>
    <Content className="content-layot settings-content" style={{ height: contentHeight, top: contentTop, overflowY: 'auto' }}>
      {loading ? <BrandLoader /> : initialError ? <LoadFailure recoverOnResume onRetry={() => { setLoading(true); setInitialError(false); setAttempt(value => value + 1); }} /> : <div className="settings-list" aria-busy={Boolean(busy)}>
        {feedback && <div className="settings-error" role="alert" ref={feedbackRef} tabIndex={-1}><AlertCircleIcon /><span>{t(feedback)}</span></div>}
        {editField('username', 'user_name')}
        {editField('name', 'first_name')}
        <section {...groupProps('photo')}><Row id="photo" label={s('photo')} onClick={() => fileInput.current?.click()} disabled={Boolean(busy)}>
          <button type="button" className="settings-icon-button settings-avatar" aria-label={s('photo')}
            aria-busy={busy === 'photo'}
            onClick={() => fileInput.current?.click()} disabled={Boolean(busy)}>
            <UserImage picture={user.picture} />
          </button>
          <input ref={fileInput} type="file" hidden accept={IMAGE_PICKER_ACCEPT} onChange={upload} />
        </Row></section>
        <section {...groupProps('language')}><Row id="language" label={s('language')}
          onClick={() => { if (!pending.current) { setOpen(null); setLanguageOpen(value => !value); } }} disabled={Boolean(busy)}>
          <SettingsPicker id="settings-language" label={s('language')} compact options={languageOptions} value={user.lng_code || i18n.resolvedLanguage}
            open={languageOpen && !busy} onOpenChange={value => { if (!pending.current) { setOpen(null); setLanguageOpen(value); } }}
            disabled={Boolean(busy) && busy !== 'language'} busy={busy === 'language'}
            onChange={value => { if (value !== user.lng_code) saveFields({ lng_code: value }); }} />
        </Row></section>
        {disclosure('location', s('location'), <LocationIcon />, countryPolicy.loading ? <BrandLoader />
          : !countryPolicy.policy?.available ? <>
            <p className="settings-note">{t('location:policyUnavailable')}</p>
            <LoadFailure onRetry={countryPolicy.refresh} recoverOnResume />
          </> : !countryPolicy.policy.can_change ? <p className="settings-note" role="status">
            {t('location:locked', { date: new Date(countryPolicy.policy.locked_until).toLocaleString(i18n.resolvedLanguage, {
              year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) })}
          </p> : countryPolicy.needsAcceptance ? <>
            <p className="settings-note">{t('location:confirmationNeeded')}</p>
            <button type="button" className="settings-icon-button" aria-label={t('location:confirmationNeeded')}
              onClick={countryPolicy.showWarning}><AlertCircleIcon /></button>
          </> : <>
          <SettingsPicker id="settings-country" label={s('country')} placeholder={s('chooseCountry')} searchable
            searchPlaceholder={s('countrySearch')} emptyLabel={s('noCountries')}
            value={draft.country_code} options={countryOptions} disabled={Boolean(busy)} open={countryOpen} onOpenChange={setCountryOpen}
            onChange={value => { geoGeneration.current += 1; setLocating(false); setFeedback(''); setDraft(previous => ({ ...previous, country_code: value })); }} />
          <button type="button" className="settings-location-detect" onClick={detectCountry} disabled={Boolean(busy) || locating} aria-busy={locating}>
            <span>{s(locating ? 'locating' : 'useLocation')}</span><LocationIcon />
          </button>
          {['settings:locationDenied', 'location:locationDisabled'].includes(feedback) && canOpenLocationSettings() &&
            <button type="button" className="settings-location-detect" onClick={() => openLocationSettings().catch(() => setFeedback('settings:locationUnavailable'))}>
              <span>{t('location:openSettings')}</span><LocationIcon />
            </button>}
          <p className="confirmation-question settings-confirm-caption">{t('brand:saveLocationQuestion')}</p>
          <ControlButton action="save" onOK={saveLocation} onCancel={closeGroup} busy={busy === 'location' || busy === 'profile'} disabledCancel={Boolean(busy)} disabledOk={!canSaveLocation} />
        </>, countryOptions.find(item => item.value === user.country_code)?.label)}
        {disclosure('notifications', s('notifications'), <EditIcon />, preferences?.notifications ? <>
          {preferences.in_app && <h2 className="settings-notification-heading">{t('inApp:onDevice')}</h2>}
          <DevicePushStatus />
          {['likes', 'reposts', 'subscriptions', ...(Object.hasOwn(preferences.notifications, 'author_comments') ? ['author_comments'] : [])].map(key => <div className="settings-notification" key={key}>
            <span id={`settings-notify-${key}`}>{key === 'author_comments' ? t('notifications:authorCommentsSetting') : s(key)}</span>
            <button type="button" className="settings-switch" role="switch" aria-checked={Boolean(notificationDraft[key])}
              aria-labelledby={`settings-notify-${key}`} disabled={Boolean(busy)}
              onClick={() => toggleNotification(key)}><span /></button>
          </div>)}
          {preferences.in_app && <section className="settings-in-app" aria-labelledby="settings-in-app-heading">
            <h2 id="settings-in-app-heading" className="settings-notification-heading">{t('inApp:inApp')}</h2>
            {['tips', 'events'].map(key => <div className="settings-notification" key={key}>
              <span id={`settings-in-app-${key}`}>{t(`inApp:${key}`)}</span>
              <button type="button" className="settings-switch" role="switch" aria-checked={Boolean(inAppDraft[key])}
                aria-labelledby={`settings-in-app-${key}`} disabled={Boolean(busy)}
                onClick={() => toggleInApp(key)}><span /></button>
            </div>)}
          </section>}
        </> : unavailable)}
        {disclosure('opinions', s('opinions'), <EditIcon />, actions(['hide_opinions', 'clear_opinions']))}
        {disclosure('account', s('account'), <EditIcon />, <>
          {actions(['exit', 'exit_all', 'delete'])}
          <AccountProtection active={open === 'account'} userId={user.id} />
          <button type="button" className="settings-action settings-legal-link" onClick={manageLegalConsent}>
            <span>{legalCopy(i18n.language).settings}</span><BackIcon />
          </button>
          {['terms', 'privacy'].map(document => <Link key={document} className="settings-action settings-legal-link"
            to={url(`${APP_ROUTES.STATIC}/${document}`)}
            onClick={event => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              // Remember the disclosure on this history entry before Link
              // opens the document. Both the app Back and browser Back return
              // here without inserting a duplicate settings page.
              navigate(`${location.pathname}${location.search}`, { replace: true,
                state: { ...location.state, settingsSection: 'account' } });
            }}>
            <span>{t(`login.${document}`)}</span><BackIcon />
          </Link>)}
        </>)}
      </div>}
    </Content>
    <Footer ref={footer} data-inactive={Boolean(focusGroup)} inert={focusGroup ? '' : undefined}><ButtonPanelReaction /></Footer>
  </Layout>;
}
